/**
 * English: Flat device list from /api/devices/list for pickers and tables.
 * Thai: รายการอุปกรณ์แบบแบน จาก /api/devices/list สำหรับ picker และตาราง
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from './useApi';
import { useStation } from '../context/StationContext';
import { ENDPOINTS } from '../config/api';
import { DeviceInfo } from '../types';

interface UseDeviceListResult {
  devices: DeviceInfo[];
  orgNames: string[];
  deviceTypes: string[];
  loading: boolean;
  /** True while station list is loading or no station id resolved yet */
  awaitingStation: boolean;
  refresh: () => Promise<void>;
}

export function useDeviceList(stationIdOverride?: string): UseDeviceListResult {
  const { get } = useApi();
  const { activeStationId, stations, loading: stationListLoading } = useStation();
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const effectiveStationId = useMemo(() => {
    const override = stationIdOverride?.trim();
    if (override) return override;
    return activeStationId || stations[0]?.id || '';
  }, [stationIdOverride, activeStationId, stations]);

  const awaitingStation =
    stationIdOverride != null && stationIdOverride.trim() !== '' ? false : stationListLoading;

  const refresh = useCallback(async () => {
    if (!effectiveStationId) return;
    setLoading(true);
    try {
      const data = await get(ENDPOINTS.devicesList, { station_id: effectiveStationId }) as {
        devices?: DeviceInfo[];
      };
      setDevices(data.devices || []);
    } catch (err) {
      console.error('[useDeviceList] Failed to load device list:', err);
    } finally {
      setLoading(false);
    }
  }, [get, effectiveStationId]);

  useEffect(() => {
    if (!effectiveStationId) {
      setDevices([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    get(ENDPOINTS.devicesList, { station_id: effectiveStationId })
      .then((data) => {
        if (!cancelled) setDevices((data as { devices?: DeviceInfo[] }).devices || []);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load device list:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [get, effectiveStationId]);

  const orgNames = [...new Set(devices.map((d) => d.org_name).filter(Boolean))];
  const deviceTypes = [...new Set(devices.map((d) => d.device_type).filter(Boolean))];

  return { devices, orgNames, deviceTypes, loading, awaitingStation, refresh };
}
